import { useEffect, useRef } from "react";
import { toast } from "sonner";

import { usePlatform } from "@/platform/PlatformProvider";

const CHECK_INTERVAL_MS = 60 * 60 * 1000; // hourly, per proposal 16
const TOAST_ID = "app-update-ready";

/**
 * Silent auto-update watcher (proposal 16). Checks the platform's update feed on
 * launch and hourly; staging happens in the background (desktop downloads + verifies
 * the signed bundle) and the user is interrupted exactly once, with a persistent
 * toast: "vX.Y.Z is ready — Restart now / Later". "Later" re-offers on next launch.
 */
export function AppUpdateWatcher() {
  const { updates } = usePlatform();
  const busy = useRef(false);

  useEffect(() => {
    if (!updates) return;

    const check = async () => {
      if (busy.current) return;
      busy.current = true;
      try {
        const staged = await updates.checkAndStage();
        if (staged) {
          toast(`Vybe Trader v${staged.version} is ready`, {
            id: TOAST_ID,
            description: "Downloaded in the background — applies on restart.",
            duration: Infinity,
            action: { label: updates.applyLabel, onClick: () => void updates.apply() },
            cancel: { label: "Later", onClick: () => {} },
          });
        }
      } catch {
        // Update checks must never surface errors into a trading session.
      } finally {
        busy.current = false;
      }
    };

    void check();
    const timer = setInterval(() => void check(), CHECK_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [updates]);

  return null;
}
